import type { Nullable } from '@iheartjane/types';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useParams } from 'react-router';
import { z } from 'zod';

import type {
  BloomFontFamily,
  BloomFooterTemplate,
  BloomFooterTemplateFormState,
  BloomOperatorConfig,
  BloomPartnerScript,
  BloomRecursiveLink,
} from '@jane/business-admin/types';
import { zBloomOperatorConfig } from '@jane/business-admin/types';
import { fetchWithDecode } from '@jane/business-admin/util';
import { janeApiV2 } from '@jane/shared/data-access';

type ColorHash = {
  contrast?: string;
  main?: string;
};

type AgeGateType = {
  cookie_name: Nullable<string>;
  enabled: Nullable<boolean>;
  message: Nullable<string>;
  store_ids: Nullable<number[]>;
};

export type BloomTheme = Partial<{
  age_gate: Partial<{
    date_picker: Partial<AgeGateType>;
    default: Nullable<'date_picker' | 'yes_no'>;
    enabled: Nullable<boolean>;
    yes_no: Partial<AgeGateType>;
  }>;
  colors: Partial<{
    deals: ColorHash | undefined;
    header: ColorHash | undefined;
    lineage: {
      cbd?: ColorHash;
      hybrid?: ColorHash;
      indica?: ColorHash;
      sativa?: ColorHash;
    };
    primary: ColorHash | undefined;
    secondary: ColorHash | undefined;
    text: {
      primary_inverse?: string;
    };
  }>;
  fonts: Partial<{
    body: BloomFontFamily | undefined;
    heading: BloomFontFamily | undefined;
  }>;
  footer: Partial<BloomFooterTemplate>;
  login_prompt: Partial<{
    colors: Partial<{
      button: Nullable<string>;
      button_text: Nullable<string>;
    }>;
    cta_label: Nullable<string>;
    desktop_line_1: Nullable<string>;
    desktop_line_2: Nullable<string>;
    desktop_line_3: Nullable<string>;
    header: Nullable<string>;
    mobile_copy: Nullable<string>;
    mobile_header: Nullable<string>;
  }>;
  main_menu: BloomRecursiveLink[];
}>;

export const SEO_PAGE_TYPES = [
  'featured',
  'deal',
  'category',
  'shopAll',
  'customRow',
  'pdp',
] as const;

export type SeoPageType = (typeof SEO_PAGE_TYPES)[number];

type SeoConfig = {
  description?: string;
  title?: string;
};

export type CustomSeoConfig = {
  [K in SeoPageType]?: SeoConfig;
};

type BloomUpdateParams = {
  custom_seo?: CustomSeoConfig;
  footer?: BloomFooterTemplateFormState;
  google_sso_client_id?: string;
  google_sso_client_secret?: string;
  google_tag_manager_container_id?: string;
  logo?: string;
  partner_scripts?: Array<BloomPartnerScript>;
  theme?: BloomTheme;
  theme_draft?: BloomTheme;
};

const bloomMenusUrl = () => `/managers/bloom_menus`;
const bloomMenuByStoreIdUrl = (storeId: string) =>
  `/managers/bloom_menus?store_id=${storeId}&sort=updated_at&order=desc`;

const bloomMenuUrl = (menuId: number) => `/managers/bloom_menus/${menuId}`;

export const saveCurrentBloomMenuId = (menu_id: number | string) => {
  return window.sessionStorage.setItem('current_bloom_menu', `${menu_id}`);
};

const getCurrentBloomMenuId = () => {
  return window.sessionStorage.getItem('current_bloom_menu');
};

export const useCurrentBloomMenu = () => {
  const { menu_id } = useParams();
  const sessionMenuId = getCurrentBloomMenuId();
  return Number(menu_id ?? sessionMenuId);
};

const fetchBloomMenus = async (): Promise<BloomOperatorConfig[]> => {
  const payloadSchema = z.object({
    bloom_menus: z.array(zBloomOperatorConfig),
  });

  const data = await fetchWithDecode(
    janeApiV2.get(bloomMenusUrl()),
    payloadSchema,
    bloomMenusUrl()
  );

  return data['bloom_menus'];
};

export const useFetchBloomMenus = <TResult = BloomOperatorConfig[]>({
  select,
}: {
  select?: (configs: BloomOperatorConfig[]) => TResult;
} = {}) => {
  return useQuery({
    queryFn: () => fetchBloomMenus(),
    queryKey: ['bloom_menus'],
    select,
  });
};

export const useFetchCurrentBloomMenu = () => {
  const menuId = useCurrentBloomMenu();

  return useFetchBloomMenus({
    select: (configs): BloomOperatorConfig | undefined =>
      menuId
        ? (configs.find((menu) => menu.id === menuId) ?? configs[0])
        : configs[0],
  });
};

const fetchBloomMenuByStoreId = async (storeId: string) => {
  const payloadSchema = z.object({
    bloom_menus: z.array(zBloomOperatorConfig),
  });

  const data = await fetchWithDecode(
    janeApiV2.get(bloomMenuByStoreIdUrl(storeId)),
    payloadSchema,
    bloomMenuByStoreIdUrl(storeId)
  );

  return data['bloom_menus'][0] ?? null;
};

export const useFetchBloomMenuByStoreId = (storeId: string) => {
  return useQuery({
    queryFn: () => fetchBloomMenuByStoreId(storeId),
    queryKey: ['bloom_menu_by_store_id', storeId],
    enabled: !!storeId,
  });
};

const updateBloomMenu = async (
  menuId?: number,
  updates: BloomUpdateParams = {}
) => {
  if (!menuId) {
    return null;
  }

  return await janeApiV2.patch(bloomMenuUrl(menuId), updates);
};

export const useUpdateBloomMenu = (menuId?: number) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (args: BloomUpdateParams = {}) =>
      await updateBloomMenu(menuId, args),
    mutationKey: ['update-bloom-menu', menuId],
    onError: console.error,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['bloom_menus'],
        exact: true,
      });
    },
  });
};
